/**
 * Extension Integration React Query hooks
 * Handles fetching data from the Wendigo extension's MongoDB database
 */

import { useQuery } from '@tanstack/react-query';
import { get, buildQueryString } from '@/services/api';
import { STALE_TIME } from '@/utils/constants';

// =============================================================================
// TYPES
// =============================================================================

export interface ExtensionUser {
  id: string;
  name: string;
  email: string;
}

export interface ExtensionUsersResponse {
  users: ExtensionUser[];
  total: number;
}

export interface ExtensionStatusResponse {
  connected: boolean;
  message: string;
}

export interface ClickedPlaceIdsResponse {
  placeIds: string[];
  total: number;
  filteredByUsers: string[];
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const extensionKeys = {
  all: ['extension'] as const,
  status: () => [...extensionKeys.all, 'status'] as const,
  users: () => [...extensionKeys.all, 'users'] as const,
  clickedPlaceIds: (userIds?: string[]) => [...extensionKeys.all, 'clickedPlaceIds', userIds] as const,
};

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to check if extension database is connected
 */
export function useExtensionStatus() {
  return useQuery({
    queryKey: extensionKeys.status(),
    queryFn: () => get<ExtensionStatusResponse>('/extension/status'),
    staleTime: STALE_TIME.STATS || 60000,
    retry: 1,
  });
}

/**
 * Hook to fetch extension users
 * Used for the "Hide Clicked By" filter dropdown
 */
export function useExtensionUsers() {
  return useQuery({
    queryKey: extensionKeys.users(),
    queryFn: () => get<ExtensionUsersResponse>('/extension/users'),
    staleTime: STALE_TIME.STATS || 60000,
    retry: 1,
  });
}

/**
 * Hook to fetch clicked place IDs
 * Optionally filtered by specific user IDs
 *
 * @param userIds - Optional array of user IDs to filter clicks by
 * @param enabled - Whether to enable the query (default: true)
 */
export function useClickedPlaceIds(userIds?: string[], enabled: boolean = true) {
  return useQuery({
    queryKey: extensionKeys.clickedPlaceIds(userIds),
    queryFn: () => {
      const params: Record<string, unknown> = {};
      if (userIds && userIds.length > 0) {
        params.userIds = userIds;
      }
      const queryString = buildQueryString(params);
      return get<ClickedPlaceIdsResponse>(`/extension/clicked-place-ids${queryString}`);
    },
    staleTime: 30000, // 30 seconds - clicks may change frequently
    enabled,
    retry: 1,
  });
}

/**
 * Helper hook to check if a specific place has been clicked
 * Uses the clicked place IDs from the cache
 */
export function useIsPlaceClicked(placeId: string, clickedPlaceIds?: string[]) {
  if (!clickedPlaceIds || !placeId) {
    return false;
  }
  return clickedPlaceIds.includes(placeId);
}
